import React, { useCallback } from 'react';

import type { AgentSkillAiResult } from '@aline/common';
import { AgentSkillAiResultSchema } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { Sparkles } from 'lucide-react';
import { useMatch } from 'react-router-dom';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  icon: {
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  root: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1.5),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  staticRoot: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
}));

const AgentSkillCard = React.memo(function AgentSkillCard({
  result,
}: {
  result: AgentSkillAiResult;
}) {
  const { classes } = useStyles();
  const isOnChatRoute = Boolean(useMatch({ end: false, path: routes.ChatIndex }));
  const name = result.name || 'Untitled skill';

  /**
   * From the full-page chat, take the current tab so the skill comes into focus. From
   * anywhere else, open a tab of its own rather than hijacking the one the user is in.
   */
  const openSkill = useCallback(() => {
    openLink(link.AdminAgentSkillDetail({ skillId: result.skillId }), {
      newTab: !isOnChatRoute,
      title: name,
    });
  }, [isOnChatRoute, name, result.skillId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      // Space would also scroll the chat pane.
      event.preventDefault();
      openSkill();
    },
    [openSkill]
  );

  return (
    <div
      className={classes.root}
      role="button"
      tabIndex={0}
      onClick={openSkill}
      onKeyDown={handleKeyDown}
    >
      <Sparkles className={classes.icon} size={18} />
      <div className={classes.text}>
        <Typography noWrap sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography className={classes.meta}>
          {result.action === 'created'
            ? 'Skill created as a draft. Open it to test and publish it.'
            : 'Skill draft updated. Open it to test and publish the change.'}
        </Typography>
      </div>
    </div>
  );
});

/** The card for a skill the agent saved after the user approved it; opens it in the editor. */
export const CodeAgentSkillElement = React.memo(function CodeAgentSkillElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(
    codeBlockInfo.code,
    AgentSkillAiResultSchema,
    'CodeAgentSkillElement',
    { captureErrors: !codeBlockInfo.isNodeStreaming }
  );
  if (!parsed.ok) {
    if (codeBlockInfo.isNodeStreaming)
      return (
        <div className={classes.staticRoot}>
          <Skeleton height={24} variant="text" width={160} />
          <Skeleton height={16} variant="text" width={120} />
        </div>
      );

    return (
      <div className={classes.staticRoot}>
        <Typography sx={{ fontWeight: 600 }}>Skill unavailable</Typography>
        <Typography className={classes.meta}>Could not display this skill.</Typography>
      </div>
    );
  }

  return <AgentSkillCard result={parsed.data} />;
});
